import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import {
  getWorkplaceCommsOversight,
  type WorkplaceCommsOversightData,
  type OversightDeepLink,
} from "@/lib/opsPaperworkClient";
import { defaultOversightFromDate } from "@/lib/messages/workplaceCommsOversightModel";

type Props = {
  organizationId: string;
  onNavigate?: (link: OversightDeepLink) => void;
};

const SEVERITY_OPTIONS = [
  { value: "all", label: "All severities" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "info", label: "Info" },
] as const;

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  if (!value.trim()) return new Date().toISOString();
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? new Date().toISOString() : new Date(ms).toISOString();
}

function severityBadgeClass(severity: string): string {
  if (severity === "high") return "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200";
  if (severity === "medium") return "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100";
  if (severity === "low") return "bg-sky-100 text-sky-900 dark:bg-sky-950/40 dark:text-sky-100";
  return "bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-gray-300";
}

/** Read-only trainer+ leadership view for Layer A comms accountability (WC-A3). */
export function WorkplaceCommsOversightPanel({ organizationId, onNavigate }: Props) {
  const [fromLocal, setFromLocal] = useState(() => toLocalInput(defaultOversightFromDate()));
  const [toLocal, setToLocal] = useState(() => toLocalInput(new Date().toISOString()));
  const [severity, setSeverity] = useState<(typeof SEVERITY_OPTIONS)[number]["value"]>("all");
  const [kind, setKind] = useState("");
  const [userId, setUserId] = useState("");
  const [data, setData] = useState<WorkplaceCommsOversightData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const queryParams = useMemo(
    () => ({
      from: fromLocalInput(fromLocal),
      to: fromLocalInput(toLocal),
      severity,
      kind: kind.trim() || undefined,
      user_id: userId.trim() || undefined,
    }),
    [fromLocal, toLocal, severity, kind, userId]
  );

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError("");
    const r = await getWorkplaceCommsOversight(queryParams);
    setLoading(false);
    if (!r.ok) {
      setError(r.error || "Unable to load oversight data.");
      setData(null);
      return;
    }
    setData(r.data);
  }, [organizationId, queryParams]);

  useEffect(() => {
    void load();
  }, [load]);

  const kindOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of data?.recent_nudges ?? []) set.add(row.kind);
    return [...set].sort();
  }, [data?.recent_nudges]);

  return (
    <div className="rounded-xl border border-violet-200/80 dark:border-violet-800/60 bg-violet-50/25 dark:bg-violet-950/15 px-4 py-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Communications oversight</h3>
            <InlineHelp label="About communications oversight">
              Leadership read-only view — ack backlog, recent Layer A nudges, checklist-blocked users, and stale-lead
              hygiene resolutions. No new transport; data from existing notifications and lead events.
            </InlineHelp>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="text-[11px] font-medium rounded-lg border border-gray-200 dark:border-gray-600 px-2.5 py-1 hover:bg-white dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? "…" : "Refresh"}
        </button>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5 text-[11px]">
        <label className="block text-gray-700 dark:text-gray-300">
          From
          <input
            type="datetime-local"
            className="mt-0.5 w-full rounded border border-gray-200 dark:border-gray-600 px-2 py-1 bg-white dark:bg-zinc-900"
            value={fromLocal}
            onChange={(e) => setFromLocal(e.target.value)}
          />
        </label>
        <label className="block text-gray-700 dark:text-gray-300">
          To
          <input
            type="datetime-local"
            className="mt-0.5 w-full rounded border border-gray-200 dark:border-gray-600 px-2 py-1 bg-white dark:bg-zinc-900"
            value={toLocal}
            onChange={(e) => setToLocal(e.target.value)}
          />
        </label>
        <label className="block text-gray-700 dark:text-gray-300">
          Severity
          <select
            className="mt-0.5 w-full rounded border border-gray-200 dark:border-gray-600 px-2 py-1 bg-white dark:bg-zinc-900"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as (typeof SEVERITY_OPTIONS)[number]["value"])}
          >
            {SEVERITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-gray-700 dark:text-gray-300">
          Kind
          <select
            className="mt-0.5 w-full rounded border border-gray-200 dark:border-gray-600 px-2 py-1 bg-white dark:bg-zinc-900"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="">All kinds</option>
            {kindOptions.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-gray-700 dark:text-gray-300">
          User id
          <input
            type="text"
            placeholder="Optional UUID filter"
            className="mt-0.5 w-full rounded border border-gray-200 dark:border-gray-600 px-2 py-1 bg-white dark:bg-zinc-900 font-mono"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </label>
      </div>

      {error ? <p className="text-[11px] text-red-600 dark:text-red-300">{error}</p> : null}

      {data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Ack backlog (org)" value={data.diagnostics.pending_required_ack_count} />
            <StatCard
              label="Users blocked on checklist"
              value={data.diagnostics.blocked_checklist_user_count}
              hint={data.diagnostics.ack_blocking_enabled ? "Ack gate on" : "Ack gate off"}
            />
            <StatCard label="Recent nudges" value={data.recent_nudges.length} />
            <StatCard label="Hygiene resolutions" value={data.hygiene_resolutions.length} />
          </div>

          {data.blocked_checklist_users.length > 0 ? (
            <Section title="Blocked checklist users">
              <ul className="text-[11px] space-y-1">
                {data.blocked_checklist_users.map((row) => (
                  <li key={row.user_id} className="flex flex-wrap items-center gap-2">
                    <SeverityPill severity={row.severity} />
                    <span className="font-medium">{row.display_name}</span>
                    <span className="text-gray-500">{row.pending_ack_count} pending ack(s)</span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {data.ack_backlog_by_user.length > 0 ? (
            <Section title="Ack backlog by user">
              <ul className="text-[11px] space-y-1">
                {data.ack_backlog_by_user.slice(0, 12).map((row) => (
                  <li key={row.user_id}>
                    {row.display_name} — {row.pending_ack_count} open
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section title="Recent nudges">
            {data.recent_nudges.length === 0 ? (
              <p className="text-[11px] text-gray-500">No nudges in range.</p>
            ) : (
              <ul className="text-[11px] space-y-2">
                {data.recent_nudges.slice(0, 25).map((row) => (
                  <li key={row.id} className="rounded-lg border border-gray-200/80 dark:border-gray-700 px-2 py-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <SeverityPill severity={row.severity} />
                      <span className="font-mono text-[10px] text-gray-500">{row.kind}</span>
                      <span className="text-gray-500">{new Date(row.created_at).toLocaleString()}</span>
                      <span className="font-medium">{row.display_name}</span>
                      {row.requires_ack && !row.acknowledged_at ? (
                        <span className="text-amber-700 dark:text-amber-300">unacked</span>
                      ) : null}
                    </div>
                    <p className="font-medium mt-0.5">{row.title}</p>
                    <DeepLinkActions link={row.deep_link} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Stale-lead hygiene resolutions">
            {data.hygiene_resolutions.length === 0 ? (
              <p className="text-[11px] text-gray-500">No hygiene resolutions in range.</p>
            ) : (
              <ul className="text-[11px] space-y-2">
                {data.hygiene_resolutions.slice(0, 25).map((row) => (
                  <li key={row.id} className="rounded-lg border border-gray-200/80 dark:border-gray-700 px-2 py-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <SeverityPill severity={row.severity} />
                      <span className="font-medium">{row.action}</span>
                      {row.cadence_days != null ? (
                        <span className="text-gray-500">{row.cadence_days}d tier</span>
                      ) : null}
                      <span className="text-gray-500">{new Date(row.created_at).toLocaleString()}</span>
                      <span>{row.actor_display_name}</span>
                    </div>
                    <p className="mt-0.5">
                      Lead: <span className="font-medium">{row.lead_label}</span>
                      {row.reason ? <span className="text-gray-600 dark:text-gray-400"> — {row.reason}</span> : null}
                    </p>
                    <DeepLinkActions link={row.deep_link} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-200 mb-1">{title}</p>
      {children}
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-zinc-950/50 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">{value}</p>
      {hint ? <p className="text-[10px] text-gray-500 dark:text-gray-500">{hint}</p> : null}
    </div>
  );
}

function SeverityPill({ severity }: { severity: string }) {
  return (
    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${severityBadgeClass(severity)}`}>
      {severity}
    </span>
  );
}

function DeepLinkActions({
  link,
  onNavigate,
}: {
  link: OversightDeepLink;
  onNavigate?: (link: OversightDeepLink) => void;
}) {
  const hasTarget = Boolean(link.lead_id || link.deal_id || link.thread_id || link.task_id);
  if (!hasTarget) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {link.lead_id ? (
        <DeepLinkButton label="Open lead" onClick={() => onNavigate?.({ lead_id: link.lead_id })} />
      ) : null}
      {link.deal_id ? (
        <DeepLinkButton label="Open deal" onClick={() => onNavigate?.({ deal_id: link.deal_id })} />
      ) : null}
      {link.thread_id ? (
        <DeepLinkButton label="Open thread" onClick={() => onNavigate?.({ thread_id: link.thread_id })} />
      ) : null}
      {link.task_id ? (
        <DeepLinkButton label="Open task" onClick={() => onNavigate?.({ task_id: link.task_id })} />
      ) : null}
    </div>
  );
}

function DeepLinkButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="text-[10px] font-medium underline opacity-80 hover:opacity-100" onClick={onClick}>
      {label}
    </button>
  );
}
